import { useEffect, useState } from "react";
import axios from "axios";
import { Download, Loader2, X } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { API } from "@/context/AuthContext";
import { inr, inrShort } from "@/pages/Dashboard";

const KIND_LABEL = { matched: "Invoiced · matched", credit: "Credit memo / no delivery", foc: "FOC / un-invoiced issue" };
const KIND_CLS = { matched: "bg-sky-50 text-sky-800", credit: "bg-slate-100 text-slate-700", foc: "bg-amber-50 text-amber-800" };
const COLS = ["kind", "billing_date", "invoice", "item", "doc", "delivery", "movement_type", "material", "description", "customer", "customer_name", "wbs", "wbs_description", "project", "sales_qty", "sales_value", "cons_value", "margin", "margin_pct"];

const toCsv = (lines) => {
  const esc = (v) => (v == null ? "" : /[",\n]/.test(String(v)) ? `"${String(v).replace(/"/g, '""')}"` : String(v));
  return [COLS.join(","), ...lines.map((l) => COLS.map((c) => esc(l[c])).join(","))].join("\n");
};

/** Side panel listing the individual transactions behind a KPI block, a chart bar or a table row. */
export function MarginDrill({ drill, params, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    if (!drill) return undefined;
    const ctrl = new AbortController();
    setD(null); setErr("");
    axios.get(`${API}/sales/margin/lines`, { params: { ...params, kind: drill.kind, month: drill.month, dim: drill.dim, key: drill.key }, signal: ctrl.signal })
      .then((r) => setD(r.data)).catch((e) => { if (!axios.isCancel(e)) setErr(e.response?.data?.detail || "Could not load transactions"); });
    return () => ctrl.abort();
  }, [drill, params]);

  const download = () => {
    if (!d) return;
    const blob = new Blob([toCsv(d.lines)], { type: "text/csv" });
    const url = URL.createObjectURL(blob); const a = document.createElement("a");
    a.href = url; a.download = `margin_lines_${drill.kind}${drill.month ? `_${drill.month}` : ""}${drill.key ? `_${String(drill.key).replace(/[^\w-]+/g, "_")}` : ""}.csv`; a.click(); URL.revokeObjectURL(url);
  };
  const t = d?.totals;
  return (
    <Sheet open={!!drill} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-[96vw] xl:max-w-[1400px] overflow-y-auto p-0" data-testid="margin-drill">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between gap-3">
            <div>
              <SheetTitle className="text-lg font-bold text-slate-900" data-testid="margin-drill-title">{drill?.title || d?.title || "Transactions"}</SheetTitle>
              <SheetDescription className="text-xs text-slate-500">
                {d ? `${d.count.toLocaleString("en-IN")} line${d.count === 1 ? "" : "s"}${d.truncated ? " (first 2,000 shown — narrow the filters or download)" : ""}` : "Loading…"}
                {drill?.month ? ` · month ${drill.month}` : ""}{drill?.key ? ` · ${drill.dim}: ${drill.key}` : ""} · {params?.from_date} → {params?.to_date}
              </SheetDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={download} disabled={!d} data-testid="margin-drill-csv" className="border-slate-300"><Download className="h-3.5 w-3.5 mr-1.5" /> CSV</Button>
              <Button variant="ghost" size="sm" onClick={onClose} data-testid="margin-drill-close"><X className="h-4 w-4" /></Button>
            </div>
          </div>
          {t && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3" data-testid="margin-drill-totals">
              {[["Sales", inrShort(t.sales_value)], ["COGS", inrShort(t.cogs)], ["Margin", inrShort(t.margin)], ["Margin %", t.margin_pct == null ? "—" : `${t.margin_pct}%`]].map(([l, v]) => <div key={l} className="rounded border border-slate-200 bg-slate-50 px-3 py-1.5"><p className="text-[10px] uppercase tracking-wider text-slate-500">{l}</p><p className="text-sm font-black tabular-nums text-slate-900">{v}</p></div>)}
            </div>
          )}
        </SheetHeader>
        <div className="px-5 py-4">
          {err && <p className="text-sm text-red-600" data-testid="margin-drill-error">{err}</p>}
          {!d && !err && <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Fetching transactions…</p>}
          {d && (
            <Table data-testid="margin-drill-table">
              <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Date</TableHead><TableHead>Invoice / Mat. doc</TableHead><TableHead>Delivery</TableHead><TableHead>Material</TableHead><TableHead>Customer</TableHead><TableHead>WBS</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Sales</TableHead><TableHead className="text-right">COGS</TableHead><TableHead className="text-right">Margin</TableHead><TableHead className="text-right">%</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.lines.map((l, i) => (
                  <TableRow key={i} data-testid={`margin-drill-row-${i}`}>
                    <TableCell><span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold whitespace-nowrap ${KIND_CLS[l.kind] || ""}`}>{KIND_LABEL[l.kind] || l.kind}</span></TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{l.billing_date}</TableCell>
                    <TableCell className="font-mono text-xs whitespace-nowrap">{l.invoice ? <>{l.invoice}<span className="text-slate-400">/{l.item}</span></> : <>{l.doc}<span className="text-slate-400"> · {l.movement_type}</span></>}</TableCell>
                    <TableCell className="font-mono text-xs">{l.delivery}</TableCell>
                    <TableCell><p className="text-xs">{l.description}</p><p className="font-mono text-[10px] text-slate-500">{l.material}</p></TableCell>
                    <TableCell className="text-xs"><p>{l.customer_name}</p><p className="font-mono text-[10px] text-slate-500">{l.customer}</p></TableCell>
                    <TableCell className="text-xs"><p className="font-mono">{l.wbs}</p><p className="text-[10px] text-slate-500">{l.wbs_description}</p></TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{l.sales_qty || (l.kind === "foc" ? l.cons_qty : "")}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{l.sales_value ? inr(l.sales_value) : ""}</TableCell>
                    <TableCell className="text-right tabular-nums text-xs">{l.cons_value ? inr(l.cons_value) : ""}</TableCell>
                    <TableCell className={`text-right tabular-nums text-xs font-semibold ${l.margin < 0 ? "text-red-600" : ""}`}>{inr(l.margin)}</TableCell>
                    <TableCell className="text-right text-xs">{l.margin_pct == null ? "—" : `${l.margin_pct}%`}</TableCell>
                  </TableRow>
                ))}
                {!d.lines.length && <TableRow><TableCell colSpan={12} className="text-center text-slate-400 py-8">No transactions</TableCell></TableRow>}
              </TableBody>
            </Table>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
